import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useLang, translateFr as tfr } from "@/i18n";
import { StepperBar } from "./create/StepperBar";
import { ContractForm } from "./create/ContractForm";
import { IntelligencePanel } from "./create/IntelligencePanel";
import { normName, parseAmount, isIsoDate } from "./create/contractCreateShared";

const EMPTY = { title: "", contract_type: "representation", player_id: "", player_name: "", counterparty: "", start_date: "", end_date: "", amount: "", currency: "EUR", commission_percent: "", description: "" };

const FOCUS_MAP = { title: "title", athlete_name: "player", counterparty: "counterparty", start_date: "start_date", end_date: "end_date", value_amount: "amount", commission_percent: "commission", currency: "currency" };

export const NewContractDialog = ({ open, onOpenChange, onSaved, contract = null, initialPlayerId = "", extraction = null, importFile = null, onBack = null, onImportRequest = null }) => {
  const { tf, locale } = useLang();
  const isEdit = Boolean(contract);
  const mode = isEdit ? "edit" : extraction ? "import" : "manual";
  const [form, setForm] = useState(EMPTY);
  const [players, setPlayers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [highlightKey, setHighlightKey] = useState(null);
  const fieldRefs = useRef({});
  const hlTimer = useRef(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      const [pr, cr] = await Promise.all([
        api.get("/players").catch(() => ({ data: [] })),
        api.get("/clubs").catch(() => ({ data: [] })),
      ]);
      if (!alive) return;
      const playersList = pr.data || [];
      setPlayers(playersList);
      setClubs(cr.data || []);
      if (isEdit) {
        setForm({
          title: contract.title || "", contract_type: contract.contract_type || "representation",
          player_id: contract.player_id || "", player_name: contract.player_name || "",
          counterparty: contract.counterparty || "",
          start_date: contract.start_date || "", end_date: contract.end_date || "",
          amount: contract.amount_cents != null ? new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(contract.amount_cents / 100) : "",
          currency: contract.currency || "EUR",
          commission_percent: contract.commission_percent != null ? String(contract.commission_percent) : "",
          description: contract.description || "",
        });
      } else if (extraction) {
        const byKey = {};
        (extraction.fields || []).forEach((f) => { if (!byKey[f.key]) byKey[f.key] = f; });
        let player_id = "", player_name = "";
        const an = (byKey.athlete_name?.value || "").trim();
        if (an) {
          const m = playersList.find((p) => normName(p.name) === normName(an));
          if (m) player_id = m.id; else player_name = an;
        }
        const amt = byKey.value_amount ? parseAmount(byKey.value_amount.value) : null;
        const curRaw = (byKey.currency?.value || "").toUpperCase();
        const comm = byKey.commission_percent ? parseAmount(byKey.commission_percent.value) : null;
        setForm({
          ...EMPTY,
          title: byKey.title?.value || "",
          player_id, player_name,
          counterparty: byKey.counterparty?.value || "",
          start_date: isIsoDate(byKey.start_date?.value) ? byKey.start_date.value : "",
          end_date: isIsoDate(byKey.end_date?.value) ? byKey.end_date.value : "",
          amount: amt !== null ? new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(amt) : "",
          currency: ["EUR", "USD", "GBP", "CHF"].includes(curRaw) ? curRaw : "EUR",
          commission_percent: comm !== null ? String(comm) : "",
        });
      } else {
        setForm({ ...EMPTY, player_id: initialPlayerId || "" });
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contract, isEdit, initialPlayerId, extraction]);

  useEffect(() => () => clearTimeout(hlTimer.current), []);

  const patch = (obj) => setForm((f) => ({ ...f, ...obj }));
  const registerRef = (k) => (el) => { fieldRefs.current[k] = el; };

  const currentOf = useMemo(() => (key) => {
    switch (key) {
      case "title": return form.title;
      case "athlete_name": return form.player_id ? (players.find((p) => p.id === form.player_id)?.name || "") : form.player_name;
      case "counterparty": return form.counterparty;
      case "start_date": return form.start_date;
      case "end_date": return form.end_date;
      case "value_amount": { const n = parseAmount(form.amount); return n === null ? "" : String(n); }
      default: return null;
    }
  }, [form, players]);

  const focusField = (extKey) => {
    const k = FOCUS_MAP[extKey];
    if (!k) return;
    const el = fieldRefs.current[k];
    if (el) {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      el.focus({ preventScroll: true });
    }
    setHighlightKey(k);
    clearTimeout(hlTimer.current);
    hlTimer.current = setTimeout(() => setHighlightKey(null), 1500);
  };

  const titleOk = Boolean(form.title.trim());
  const missing = [
    [!currentOf("athlete_name"), tfr("Athlète")],
    [!form.counterparty, "Contrepartie"],
    [!form.start_date || !form.end_date, "Dates"],
    [parseAmount(form.amount) === null, "Montant"],
  ].filter(([m]) => m).map(([, l]) => tf(l));

  const submit = async () => {
    if (!titleOk) { toast.error(tf("Le titre est requis pour créer le contrat.")); return; }
    setSaving(true);
    try {
      const amountNum = parseAmount(form.amount);
      const payload = {
        title: form.title.trim(), contract_type: form.contract_type,
        counterparty: (form.counterparty || "").trim() || null,
        start_date: form.start_date || null, end_date: form.end_date || null,
        amount_cents: amountNum !== null ? Math.round(amountNum * 100) : null,
        currency: form.currency,
        commission_percent: form.commission_percent !== "" ? parseAmount(form.commission_percent) : null,
        description: (form.description || "").trim() || null,
      };
      if (isEdit) {
        if (form.player_id !== (contract.player_id || "")) payload.player_id = form.player_id || null;
      } else {
        payload.player_id = form.player_id || null;
        if (!form.player_id) payload.player_name = (form.player_name || "").trim() || null;
        if (mode === "import" && extraction) {
          payload.import_extraction = { filename: extraction.filename || (importFile?.name || ""), fields: extraction.fields || [] };
        }
      }
      const res = isEdit ? await api.patch(`/contracts/${contract.id}`, payload) : await api.post("/contracts", payload);
      if (!isEdit && importFile) {
        try {
          const fd = new FormData();
          fd.append("file", importFile);
          fd.append("doc_kind", "contract");
          fd.append("label", importFile.name || "document");
          await api.post(`/contracts/${res.data.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch { toast.warning(tf("Le document n'a pas pu être joint au contrat")); }
      }
      toast.success(isEdit ? tf("Contrat mis à jour") : tf("Contrat créé"));
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || tf("Erreur"));
    } finally { setSaving(false); }
  };

  const editExtraction = isEdit && contract?.import_extraction?.fields?.length ? contract.import_extraction : null;
  const panelExtraction = mode === "import" ? extraction : editExtraction;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-0 gap-0 overflow-hidden flex flex-col w-full max-w-full h-[100dvh] max-h-[100dvh] rounded-none border-0 sm:h-auto sm:max-h-[88vh] sm:rounded-2xl sm:border sm:max-w-[720px] lg:max-w-[980px] xl:max-w-[1040px]"
        data-testid="contract-dialog">
        <div className="shrink-0 flex items-start gap-3.5 px-5 sm:px-7 pt-[calc(1.1rem+env(safe-area-inset-top))] sm:pt-5 pb-4 pr-14 border-b border-border/70 dark:border-border text-left">
          <div className="h-10 w-10 rounded-xl bg-red-500/10 border border-red-500/25 flex items-center justify-center shrink-0">
            {isEdit ? <Pencil className="h-[18px] w-[18px] text-red-500" /> : <Plus className="h-5 w-5 text-red-500" />}
          </div>
          <div className="min-w-0">
            <DialogTitle className="text-lg font-semibold tracking-[-0.01em]">
              {isEdit ? tf("Modifier le contrat") : tf("Nouveau contrat")}
            </DialogTitle>
            <DialogDescription className="text-[13px] mt-0.5">
              {isEdit ? tf("Mettez à jour les informations du contrat.") : tf("Créez un contrat manuellement ou à partir d'un document.")}
            </DialogDescription>
          </div>
        </div>

        {!isEdit && (
          <div className="shrink-0 px-5 sm:px-7 py-3 border-b border-border/70 dark:border-border">
            <StepperBar step={mode === "import" ? 2 : 1} tf={tf} />
          </div>
        )}

        {mode === "import" && extraction && (
          <p className="sr-only" role="status">{tf("Analyse terminée")} — {extraction.detected} {tf("informations")}</p>
        )}

        <div className="flex-1 min-h-0 w-full max-w-full overflow-y-auto overflow-x-hidden overscroll-contain [-webkit-overflow-scrolling:touch] px-5 sm:px-7 py-5" data-testid="contract-form-scroll">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] items-start">
            <ContractForm form={form} patch={patch} players={players} clubs={clubs} tf={tf} locale={locale}
              registerRef={registerRef} highlightKey={highlightKey} mode={mode} />
            <IntelligencePanel mode={mode} extraction={panelExtraction} contract={contract} currentOf={currentOf}
              tf={tf} locale={locale} onFocusField={focusField} onImportRequest={mode === "manual" ? onImportRequest : null} />
          </div>
        </div>

        <div className="shrink-0 w-full max-w-full flex items-center gap-2 px-5 sm:px-7 pt-3 pb-[calc(0.85rem+env(safe-area-inset-bottom))] sm:pb-4 border-t border-border/70 dark:border-border bg-background">
          <div className="flex-1 min-w-0 hidden min-[420px]:block">
            {!titleOk ? (
              <p className="text-xs text-red-500 truncate" data-testid="cta-blocked-hint">{tf("Le titre est requis pour créer le contrat.")}</p>
            ) : missing.length > 0 ? (
              <p className="text-xs text-amber-500/90 truncate" data-testid="completeness-hint">{tf("À compléter")} : {missing.join(" · ")}</p>
            ) : mode === "import" && importFile ? (
              <p className="text-xs text-muted-foreground truncate">{tf("Le document importé sera joint au contrat.")}</p>
            ) : null}
          </div>
          {mode === "import" && onBack && (
            <Button variant="outline" className="h-11 sm:h-10" data-testid="contract-back-step-btn"
              onClick={() => { onBack(); onOpenChange(false); }}>
              <ArrowLeft className="h-4 w-4 mr-1" /> {tf("Retour")}
            </Button>
          )}
          <Button variant="ghost" className="h-11 sm:h-10" onClick={() => onOpenChange(false)} data-testid="contract-cancel-btn">{tf("Annuler")}</Button>
          <Button className="h-11 sm:h-10" data-testid={mode === "import" ? "import-create-btn" : "contract-save-btn"}
            onClick={submit} disabled={saving || !titleOk}>
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {saving ? tf("Enregistrement…")
              : isEdit ? tf("Enregistrer")
                : mode === "import" ? <>{tf("Confirmer et créer le contrat")} <ArrowRight className="h-4 w-4 ml-1" /></>
                  : tf("Créer le contrat")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
